// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import {
  useMlModelVote,
  type ModelVoteArm,
  type ModelVoteHoldout,
  type ModelVoteKind,
} from './useMlModelVote';

/** The vote strengths offered side by side: the canopy's size, the configured default, and a harder one. */
const DELTAS = [0.1, 0.25, 0.4];

/**
 * The A/B for letting a trained model vote on the classical detections.
 *
 * The model beats the classical detector on held-out courses but its shapes are not published;
 * a vote only moves a classical detection's confidence. This shows what that would do before
 * anybody turns it on: per kind, how many detections clear the serving floor today and with the
 * vote, and for the ones somebody has judged, how many of those are real. The judged set is small
 * by construction — the legend says why — so the page shows the counts rather than just a score.
 */
export function MlModelVotePanel({
  apiBaseUrl,
  token,
}: {
  apiBaseUrl: string;
  token: string | null | undefined;
}) {
  const { t } = useI18n();
  const [version, setVersion] = useState<string | null>(null);
  const [delta, setDelta] = useState<number | null>(null);
  const { ab, loading, error } = useMlModelVote(apiBaseUrl, token, true, version, delta);

  return (
    <View style={styles.card} testID="ml-model-vote-panel">
      <Text style={styles.title}>{t('mlModelVote.title')}</Text>
      <Text style={styles.legend}>{t('mlModelVote.legend')}</Text>

      {loading && !ab ? (
        <ActivityIndicator color={appColors.accent.primary} />
      ) : error === 'unavailable' ? (
        <Text style={styles.notice}>{t('mlModelVote.unavailable')}</Text>
      ) : error ? (
        <Text style={styles.notice}>{t('mlModelVote.failed')}</Text>
      ) : !ab || ab.version === null ? (
        <Text style={styles.notice} testID="ml-model-vote-empty">
          {t('mlModelVote.empty')}
        </Text>
      ) : (
        <>
          <Text style={ab.inForce ? styles.badge : styles.meta} testID="ml-model-vote-in-force">
            {ab.inForce
              ? t('mlModelVote.inForce', { version: ab.inForce.version, delta: ab.inForce.delta.toFixed(2) })
              : t('mlModelVote.notInForce')}
          </Text>

          <Text style={styles.label}>{t('mlModelVote.model')}</Text>
          <View style={styles.chips}>
            {ab.versions.map((choice) => (
              <Choice
                key={choice.version}
                label={choice.nickname ?? choice.version}
                selected={choice.version === ab.version}
                onPress={() => setVersion(choice.version)}
                testID={`ml-model-vote-version-${choice.version}`}
              />
            ))}
          </View>

          <Text style={styles.label}>{t('mlModelVote.delta')}</Text>
          <View style={styles.chips}>
            {DELTAS.map((value) => (
              <Choice
                key={value}
                label={value.toFixed(2)}
                selected={Math.abs(value - ab.delta) < 1e-9}
                onPress={() => setDelta(value)}
                testID={`ml-model-vote-delta-${value}`}
              />
            ))}
          </View>

          <Text style={styles.meta}>
            {t('mlModelVote.coverage', {
              judged: ab.coursesJudged,
              available: ab.coursesAvailable,
              floor: ab.servedFloor.toFixed(2),
            })}
          </Text>
          {loading ? <Text style={styles.meta}>{t('mlModelVote.updating')}</Text> : null}

          <HoldoutBlock holdout={ab.holdout} />

          {ab.byKind
            .filter((kind) => kind.candidates > 0)
            .map((kind) => (
              <KindRow key={kind.kind} kind={kind} />
            ))}

          {ab.byCourse.map((course) => (
            <Text key={course.courseId} style={styles.meta} testID={`ml-model-vote-course-${course.courseId}`}>
              {t('mlModelVote.course', {
                name: course.courseName ?? course.courseId,
                without: course.clearsWithout,
                with: course.clearsWith,
                agrees: course.agrees,
                contradicts: course.contradicts,
              })}
            </Text>
          ))}
        </>
      )}
    </View>
  );
}

function KindRow({ kind }: { kind: ModelVoteKind }) {
  const { t } = useI18n();
  const judged = kind.knownReal > 0 || kind.knownFalse > 0;

  return (
    <View style={styles.row} testID={`ml-model-vote-kind-${kind.kind}`}>
      <Text style={styles.kind}>{t(`courseAnalysis.kind.${kind.kind}`)}</Text>
      <Text style={styles.meta}>
        {t('mlModelVote.votes', {
          candidates: kind.candidates,
          agrees: kind.agrees,
          contradicts: kind.contradicts,
          inconclusive: kind.inconclusive,
          notAsked: kind.notAsked,
        })}
      </Text>
      <Text style={styles.value}>
        {t('mlModelVote.clears', { without: kind.without.clears, with: kind.with.clears })}
      </Text>
      {judged ? (
        <>
          <Text style={styles.value} testID={`ml-model-vote-judged-${kind.kind}`}>
            {t('mlModelVote.judged', {
              real: kind.knownReal,
              false: kind.knownFalse,
              precisionWithout: share(kind.without, 'judgedPrecision'),
              precisionWith: share(kind.with, 'judgedPrecision'),
              recallWithout: share(kind.without, 'judgedRecall'),
              recallWith: share(kind.with, 'judgedRecall'),
            })}
          </Text>
          <Text style={styles.meta}>
            {t('mlModelVote.judgedCounts', {
              fpWithout: kind.without.clearsFalse,
              fpWith: kind.with.clearsFalse,
              missedWithout: kind.without.hiddenReal,
              missedWith: kind.with.hiddenReal,
            })}
          </Text>
        </>
      ) : (
        <Text style={styles.meta}>{t('mlModelVote.notJudged')}</Text>
      )}
    </View>
  );
}

/**
 * The vote on held-out courses — the half of the A/B whose ground truth is not thinned by
 * drop_already_known, so the one to read before naming a model to vote.
 */
function HoldoutBlock({ holdout }: { holdout: ModelVoteHoldout | null }) {
  const { t } = useI18n();

  if (holdout === null) {
    return (
      <Text style={styles.meta} testID="ml-model-vote-holdout-none">
        {t('mlModelVote.holdoutNone')}
      </Text>
    );
  }

  if (!holdout.scored) {
    return (
      <Text style={styles.meta} testID="ml-model-vote-holdout-unscored">
        {t('mlModelVote.holdoutNoConfidence')}
      </Text>
    );
  }

  return (
    <View style={styles.row} testID="ml-model-vote-holdout">
      <Text style={styles.kind}>
        {t('mlModelVote.holdoutTitle', { courses: holdout.courses, provider: holdout.provider ?? '–' })}
      </Text>
      {holdout.byKind.map((kind) => (
        <Text key={kind.kind} style={styles.value} testID={`ml-model-vote-holdout-${kind.kind}`}>
          {t('mlModelVote.holdoutKind', {
            kind: t(`courseAnalysis.kind.${kind.kind}`),
            mapped: kind.mapped,
            precisionWithout: percent(kind.without.precision),
            precisionWith: percent(kind.with.precision),
            recallWithout: percent(kind.without.recall),
            recallWith: percent(kind.with.recall),
            f1Without: percent(kind.without.f1),
            f1With: percent(kind.with.f1),
          })}
        </Text>
      ))}
    </View>
  );
}

/** A share geodata computed, as a whole percentage; a dash where there was nothing to divide. */
const percent = (value: number | null): string => (value === null ? '–' : `${Math.round(value * 100)}%`);

const share = (arm: ModelVoteArm, key: 'judgedPrecision' | 'judgedRecall'): string => percent(arm[key]);

function Choice({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  testID: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onPress}
      style={[styles.chip, selected ? styles.chipSelected : null]}
      testID={testID}
    >
      <Text style={[styles.chipText, selected ? styles.chipTextSelected : null]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 14,
    gap: 10,
  },
  title: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
  legend: { color: appColors.text.secondary, fontSize: 11 },
  notice: { color: appColors.text.secondary, fontSize: 13 },
  label: {
    color: appColors.text.secondary,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: {
    height: 30,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipSelected: { borderColor: appColors.accent.primary },
  chipText: { color: appColors.text.secondary, fontSize: 11, fontWeight: '600' },
  chipTextSelected: { color: appColors.accent.primary },
  row: {
    gap: 2,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: appColors.surface.input,
  },
  kind: { color: appColors.text.primary, fontSize: 13, fontWeight: '700' },
  value: { color: appColors.text.primary, fontSize: 12 },
  meta: { color: appColors.text.secondary, fontSize: 11 },
  badge: {
    color: appColors.accent.primary,
    fontSize: 11,
    fontWeight: '700',
  },
});
